import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { SparklesIcon, HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import EmailCaptureForm from './components/EmailCaptureForm';
import FileUploadCard from './components/FileUploadCard';
import MetadataBasics from './components/MetadataBasics';
import MetadataWhenWhere from './components/MetadataWhenWhere';
import MetadataChargeStep from './components/MetadataChargeStep';
import QuestionEngine from './components/QuestionEngine';
import ExtractionSummaryCard from './components/ExtractionSummaryCard';
import AnalysisReport from './components/AnalysisReport';
import StepIndicator from './components/StepIndicator';
import PageTitle from './components/PageTitle';
import ErrorBanner from './components/ErrorBanner';
import LoadingVisual from './components/LoadingVisual';
import { useMenuAnalysis } from './hooks/useMenuAnalysis';
import type { PhaseTwoMetadataRequest } from './types';
import Header from './components/Header';
import Footer from './components/Footer';
import HowItWorks from './components/HowItWorks';
import BuiltForPros from './components/BuiltForPros';
import ReadyCta from './components/ReadyCta';
import { ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import FeedbackModal from './components/FeedbackModal';
import Toast from './components/Toast';

const Landing = ({ onStart }: { onStart: () => void }) => (
  <section className="relative z-10 mx-auto flex min-h-[80vh] max-w-5xl flex-col items-center justify-center px-6 py-24 text-center fade-in-up">
    <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium tracking-wide badge-gradient">
      <SparklesIcon className="h-4 w-4 text-[color:var(--color-primary)]" />
      <span className="text-gradient-animated text-sm md:text-base font-semibold">AI-Powered Menu Intelligence</span>
    </div>
    <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-900 md:text-7xl">
      Optimize your event
      <br />
      <Typewriter
        words={[
          'beverage',
          'food',
          'concession',
          'drink',
          'menu'
        ]}
        className="text-gradient-animated text-gradient-readable inline whitespace-nowrap"
      />
      <span className="sm:ml-1"> pricing</span> <span className="sm:inline block">instantly</span>
    </h1>
    <p className="mt-6 max-w-3xl text-base text-slate-600 md:text-xl">
      Upload your event beverage or food menu for a FREE AI-powered pricing analysis. Compare against thousands of
      events, and get actionable recommendations in minutes.
    </p>
    <button
      type="button"
      onClick={onStart}
      className="mt-12 btn-primary"
    >
      Analyze Your Menu - FREE
    </button>
    <p className="mt-4 text-sm text-slate-500">Free • No signup required • Results in minutes</p>

  </section>
);

// Removed feature cards per updated landing design

const App = () => {
  const { sessionId, extraction, analysis, metadata, step, error, setError, actions, isLoading, email, isHydrating, errorRetry, hasFeedback } =
    useMenuAnalysis();
  const [started, setStarted] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackInitialRating, setFeedbackInitialRating] = useState<'up' | 'down' | undefined>(undefined);
  const [toast, setToast] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [qTitle, setQTitle] = useState<string | null>(null);
  const [qHelp, setQHelp] = useState<string | undefined>(undefined);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        if (/\/menuanalysis\//i.test(window.location.pathname)) {
          setStarted(true);
        }
      }
    } catch {}
  }, []);

  const metadataDefaults = useMemo(() => {
    return {
      eventName: (metadata as any)?.eventName ?? (extraction as any)?.menu?.eventName ?? undefined,
      eventType: metadata?.eventType ?? extraction?.menu.eventType ?? undefined,
      eventLocation: metadata?.eventLocation ?? extraction?.menu.eventLocation ?? undefined,
      eventDate: metadata?.eventDate ?? extraction?.menu.eventDate ?? undefined,
      taxIncluded: metadata?.taxIncluded ?? extraction?.menu.taxIncluded ?? undefined,
      serviceFeeIncluded: metadata?.serviceFeeIncluded ?? extraction?.menu.serviceFeeIncluded ?? undefined,
      taxRate: (metadata as any)?.taxRate ?? (extraction as any)?.menu?.taxRate ?? undefined,
      serviceFeeRate: (metadata as any)?.serviceFeeRate ?? (extraction as any)?.menu?.serviceFeeRate ?? undefined
    } satisfies Partial<PhaseTwoMetadataRequest>;
  }, [metadata, extraction]);

  const reportMetadata = useMemo(
    () => ({
      eventName: (metadata as any)?.eventName ?? (extraction as any)?.menu?.eventName ?? undefined,
      eventType: metadata?.eventType ?? extraction?.menu.eventType ?? undefined,
      eventLocation: metadata?.eventLocation ?? extraction?.menu.eventLocation ?? undefined,
      eventDate: metadata?.eventDate ?? extraction?.menu.eventDate ?? undefined,
      taxIncluded: metadata?.taxIncluded ?? extraction?.menu.taxIncluded ?? undefined,
      serviceFeeIncluded: metadata?.serviceFeeIncluded ?? extraction?.menu.serviceFeeIncluded ?? undefined,
      taxRate: (metadata as any)?.taxRate ?? (extraction as any)?.menu?.taxRate ?? undefined,
      serviceFeeRate: (metadata as any)?.serviceFeeRate ?? (extraction as any)?.menu?.serviceFeeRate ?? undefined
    }),
    [metadata, extraction]
  );

  const pageTitle = useMemo(() => {
    switch (step) {
      case 'upload':
        return { title: 'Upload your menu', help: "Drag and drop an image (max 10MB). We'll analyze pricing and profitability across bar scenarios." };
      case 'extraction':
        return { title: 'Analyzing your menu', help: 'Our AI is working its magic. This usually takes 1 to 2 minutes.' };
      case 'review':
        return { title: 'We parsed your menu', help: undefined };
      case 'meta-form':
        return { title: 'Event Details', help: 'Help us deliver accurate benchmarks by answering few quick questions.' };
      case 'email':
        return { title: 'Send your report', help: 'You will see a report on next page and we will also email it to your inbox.' };
      case 'analysis':
        return { title: 'Generating Recommendations', help: 'Our AI is working its magic. This usually takes 2 to 4 minutes.' };
      default:
        return null;
    }
  }, [step, qTitle, qHelp]);

  const renderActiveSection = () => {
    switch (step) {
      case 'email':
        return <EmailCaptureForm onSubmit={actions.identify} isSubmitting={isLoading} />;
      case 'upload':
        return (
          <div id="labs-upload-anchor">
            <FileUploadCard
              onUpload={async (file) => {
                await actions.uploadFile(file);
                await actions.runExtraction();
              }}
              isUploading={isLoading}
              sessionEmail={email ?? undefined}
            />
          </div>
        );
      case 'extraction':
        return (
          <StatusCard
            visual={<LoadingVisual phase="extraction" />}
          />
        );
      case 'review':
        return (
          extraction && (
            <ExtractionSummaryCard
              extraction={extraction}
              onNext={actions.proceedFromReview}
            />
          )
        );
      case 'meta-form':
        return (
          <QuestionEngine
            defaults={metadata as any}
            extractionDefaults={{
              eventName: ((extraction as any)?.menu?.eventName ?? undefined) as any,
              eventType: extraction?.menu.eventType ?? undefined,
              eventDate: extraction?.menu.eventDate ?? undefined,
              eventLocation: extraction?.menu.eventLocation ?? undefined,
              taxIncluded: extraction?.menu.taxIncluded as any,
              taxRate: (extraction as any)?.menu?.taxRate,
              serviceFeeIncluded: extraction?.menu.serviceFeeIncluded as any,
              serviceFeeRate: (extraction as any)?.menu?.serviceFeeRate
            }}
            onApply={actions.applyMetadata}
            onComplete={actions.gotoEmailAfterMetadata}
            onTitleChange={(t, h) => { setQTitle(t); setQHelp(h); }}
          />
        );
      case 'meta-basics':
        return (
          <MetadataBasics
            defaults={{ eventName: (((metadata as any)?.eventName ?? (extraction as any)?.menu?.eventName) ?? undefined) as any, eventType: (metadata?.eventType ?? extraction?.menu.eventType) ?? undefined, eventDate: (metadata?.eventDate ?? extraction?.menu?.eventDate) ?? undefined }}
            onBack={actions.backToReview}
            onNext={actions.saveBasicsAndNext}
          />
        );
      case 'meta-when-where':
        return (
          <MetadataWhenWhere
            defaults={{ eventLocation: (metadata?.eventLocation ?? extraction?.menu.eventLocation) ?? undefined }}
            onBack={actions.backToBasics}
            onNext={actions.saveWhenWhereAndNext}
          />
        );
      case 'meta-tax':
        return (
          <MetadataChargeStep
            kind="tax"
            defaults={{ included: metadata?.taxIncluded ?? extraction?.menu.taxIncluded as any, rate: (metadata as any)?.taxRate ?? (extraction as any)?.menu?.taxRate }}
            onBack={actions.backToWhenWhere}
            onNext={actions.saveTaxAndNext}
          />
        );
      case 'meta-service':
        return (
          <MetadataChargeStep
            kind="service"
            defaults={{ included: metadata?.serviceFeeIncluded ?? extraction?.menu.serviceFeeIncluded as any, rate: (metadata as any)?.serviceFeeRate ?? (extraction as any)?.menu?.serviceFeeRate }}
            onBack={actions.backToTax}
            onNext={actions.saveServiceAndNext}
          />
        );
      case 'analysis':
        return (
          <StatusCard
            visual={<LoadingVisual phase="analysis" />}
          />
        );
      default:
        return null;
    }
  };

  const renderWizard = () => (
    <div className="relative z-10 px-6 pb-10 pt-8">
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
        {step === 'complete' ? (
          (() => {
            const name = (reportMetadata as any)?.eventName as string | undefined;
            const location = reportMetadata.eventLocation;
            const dateRaw = reportMetadata.eventDate;
            let dateText: string | null = null;
            if (dateRaw) {
              const d = new Date(dateRaw);
              if (!Number.isNaN(d.getTime())) {
                dateText = format(d, 'PPP');
              }
            }

            let fallbackTitle = 'Your Event';
            if (location && dateText) {
              fallbackTitle = `Your Event in ${location} on ${dateText}`;
            } else if (location) {
              fallbackTitle = `Your Event in ${location}`;
            } else if (dateText) {
              fallbackTitle = `Your Event on ${dateText}`;
            }

            return (
              <>
                <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-600">Menu Analysis Report</h3>
                <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
                  {name && name.trim().length > 0 ? name : fallbackTitle}
                </h1>
              </>
            );
          })()
        ) : (
          <>
            {pageTitle && <PageTitle title={pageTitle.title} subline={pageTitle.help} />}
            <div className="mt-6 mb-2 w-full">
              <StepIndicator current={step} />
            </div>
          </>
        )}
      </div>

      <div className="mx-auto mt-10 flex w-full max-w-5xl flex-col gap-6">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} onRetry={errorRetry} />}

        {isHydrating ? (
          <div className="fade-in-up">
            <StatusCard
              title="Resuming your session"
              description="Picking up where you left off."
              visual={<LoadingVisual minimal phase={step === 'analysis' ? 'analysis' : 'extraction'} />}
            />
          </div>
        ) : step === 'complete' ? (
          analysis && (
            <div className="fade-in-up">
              <AnalysisReport extraction={extraction} analysis={analysis} metadata={reportMetadata} />
            </div>
          )
        ) : (
          <div key={step} className="mx-auto w-full max-w-3xl fade-in-up">{renderActiveSection()}</div>
        )}

        {step === 'complete' && !isHydrating && (
          <div className="mx-auto w-full max-w-5xl">
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-6 shadow-sm">
              <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-600">Next Steps</h3>
              <div className="mt-4 flex flex-col items-center gap-4">
                {!hasFeedback && (
                  <div className="flex flex-col items-center justify-center gap-3 w-full mb-2">
                    <span className="text-sm text-slate-700">How did we do?</span>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setFeedbackInitialRating('up');
                          setFeedbackOpen(true);
                          void actions.submitFeedback('up');
                        }}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50"
                      >
                        <HandThumbUpIcon className="h-4 w-4" /> Helpful
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setFeedbackInitialRating('down');
                          setFeedbackOpen(true);
                          void actions.submitFeedback('down');
                        }}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50"
                      >
                        <HandThumbDownIcon className="h-4 w-4" /> Not helpful
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => actions.reset()}
                  className="inline-flex items-center gap-2 btn-primary"
                >
                  Analyze Another Menu
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const url = typeof window !== 'undefined' ? window.location.href : '';
                      const name = (reportMetadata as any)?.eventName as string | undefined;
                      const msg = `Hey checkout the menu analysis report${name && name.trim().length > 0 ? ` for my event ${name}` : ''}: ${url}`;
                      const mailto = `mailto:?body=${encodeURIComponent(msg)}`;
                      if (typeof window !== 'undefined') {
                        window.location.href = mailto;
                      }
                    } catch {}
                  }}
                  className="inline-flex items-center gap-2 btn-primary"
                >
                  <ArrowUpOnSquareIcon className="h-4 w-4" />
                  Share With Teammates
                </button>
                </div>
              </div>
              
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">AI can make mistakes, always review recommendations before leveraging.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen flex flex-col text-slate-900">
      <Header
        isLanding={!started}
        showShare={step === 'complete'}
        showCenterTitle={started && step !== 'complete'}
        onShare={() => {
          try {
            const url = typeof window !== 'undefined' ? window.location.href : '';
            const name = (reportMetadata as any)?.eventName as string | undefined;
            const msg = `Hey checkout the menu analysis report${name && name.trim().length > 0 ? ` for my event ${name}` : ''}: ${url}`;
            const mailto = `mailto:?body=${encodeURIComponent(msg)}`;
            if (typeof window !== 'undefined') {
              window.location.href = mailto;
            }
          } catch {}
        }}
        onAnalyze={() => {
          setStarted(true);
          // try to scroll to upload area once rendered
          setTimeout(() => {
            try {
              const el = document.getElementById('labs-upload-anchor');
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch {}
          }, 50);
        }}
      />
      <main className="flex-1">
        {toast && (
          <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} />
        )}
        <FeedbackModal
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          initialRating={feedbackInitialRating}
          onSubmit={async (rating, comment) => {
            try {
              await actions.submitFeedback(rating, comment);
              setToast({ kind: 'success', message: 'Thanks for your feedback!' });
            } catch (e) {
              setToast({ kind: 'error', message: 'Failed to submit feedback. Please try again.' });
            } finally {
              setFeedbackOpen(false);
            }
          }}
        />
        {/* Primary glow backdrop for all non-landing pages */}
        {started && <div className="page-glow" aria-hidden="true" />}

        {!started ? (
          <>
            <Landing onStart={() => setStarted(true)} />
            <HowItWorks />
            <BuiltForPros />
            <ReadyCta
              onAnalyze={() => {
                setStarted(true);
                setTimeout(() => {
                  try {
                    const el = document.getElementById('labs-upload-anchor');
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } catch {}
                }, 50);
              }}
            />
          </>
        ) : (
          renderWizard()
        )}
      </main>
      <Footer />
    </div>
  );
};

const StatusCard = ({ title, description, visual }: { title?: string; description?: string; visual?: React.ReactNode }) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-10 text-center shadow-xl backdrop-blur fade-in-up">
    <div className="pointer-events-none absolute -inset-10" />
    {title && <p className="relative z-10 text-lg font-semibold text-slate-900">{title}</p>}
    {description && <p className="relative z-10 mt-3 text-sm text-slate-600">{description}</p>}
    {visual && <div className="relative z-10 mt-6">{visual}</div>}
  </div>
);

export default App;

const Typewriter = ({ words, className }: { words: string[]; className?: string }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[index % words.length];
    const baseDelay = deleting ? 70 : 120;
    const holdDelay = 1600;

    // When a word is fully typed, hold before deleting
    if (!deleting && text === current) {
      const hold = setTimeout(() => setDeleting(true), holdDelay);
      return () => clearTimeout(hold);
    }

    const t = setTimeout(() => {
      if (!deleting) {
        setText(current.slice(0, text.length + 1));
      } else {
        const next = text.slice(0, Math.max(0, text.length - 1));
        setText(next);
        if (next.length === 0) {
          setDeleting(false);
          setIndex((i) => (i + 1) % words.length);
        }
      }
    }, baseDelay);

    return () => clearTimeout(t);
  }, [text, deleting, index, words]);

  const base = 'inline leading-tight whitespace-nowrap';
  return <span className={`${base} ${className ?? 'text-slate-900'}`}>{text}<span className="caret align-middle" aria-hidden /></span>;
};
